import { Dispatch, SetStateAction } from 'react';

import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import WorkTasks from '@atom/components/common/workTasks/WorkTasks';
import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, ListTable, Progress, Tooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import { WorkOrder, WorkOrderUserGroup } from '@atom/types/work';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { rollUpTaskUsers } from '@atom/utilities/userUtilities';

import { RESULTS_PER_PAGE_OPTIONS } from './AssetWorkOrdersContainer';

import classes from './assetWorkOrdersTable.module.css';

const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const headerSpacerAndTableFooterOffset = '6.5rem';

const styles = {
  tableContainer: {
    height: `calc(100% - ${headerSpacerAndTableFooterOffset})`,
  },
  progress: {
    height: '100%',
  },
  link: {
    fontWeight: '500',
  },
  workIdCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  pmIcon: {
    fontSize: '1.25rem',
    color: colors.neutral.gray,
    marginRight: '0.375rem',
  },
};

interface Props {
  workOrders: WorkOrder[] | undefined;
  loadingWorkOrders: boolean;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  sortDirection: 'asc' | 'desc';
  setSortDirection: Dispatch<SetStateAction<'asc' | 'desc'>>;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  rowsPerPage: number;
  setRowsPerPage: Dispatch<SetStateAction<number>>;
  totalCount: number;
}

const AssetWorkOrdersTable = ({
  workOrders,
  loadingWorkOrders,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
}: Props) => {
  const [, { Link }] = useNavigation();

  const handleSortChange = (sortField: string, direction: 'asc' | 'desc') => {
    setSortBy(sortField);
    setSortDirection(direction);
  };

  if (loadingWorkOrders) {
    return <Progress style={styles.progress} />;
  }

  return (
    <>
      <div className={classes.tableHeaderSpacer}>{`${totalCount} Work`}</div>
      <ListTable TableContainerProps={{ style: styles.tableContainer }}>
        <TableHead>
          <TableRow header>
            <TableCell
              variant="head"
              sortDirection={sortBy === 'name' ? sortDirection : false}
              onSortChange={direction => handleSortChange('name', direction)}
            >
              Work ID
            </TableCell>
            <TableCell variant="head">Tasks</TableCell>
            <TableCell variant="head">Work Template</TableCell>
            <TableCell
              variant="head"
              sortDirection={sortBy === 'statusId' ? sortDirection : false}
              onSortChange={direction =>
                handleSortChange('statusId', direction)
              }
            >
              Status
            </TableCell>
            <TableCell variant="head">Assigned To</TableCell>
            <TableCell
              variant="head"
              sortDirection={sortBy === 'dueDate' ? sortDirection : false}
              onSortChange={direction => handleSortChange('dueDate', direction)}
            >
              Due Date
            </TableCell>
            <TableCell
              variant="head"
              sortDirection={
                sortBy === 'completionDate' ? sortDirection : false
              }
              onSortChange={direction =>
                handleSortChange('completionDate', direction)
              }
            >
              Completed Date
            </TableCell>
            <TableCell
              variant="head"
              sortDirection={sortBy === 'appliedDate' ? sortDirection : false}
              onSortChange={direction =>
                handleSortChange('appliedDate', direction)
              }
            >
              Inventory Update Date
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workOrders?.map(workOrder => (
            <TableRow key={workOrder.id}>
              <TableCell>
                <span style={styles.workIdCell}>
                  {workOrder.source === 'PM' && (
                    <Tooltip title="PM-generated work">
                      <Icon style={styles.pmIcon}>autorenew</Icon>
                    </Tooltip>
                  )}
                  <Link
                    style={styles.link}
                    to={`/workOrders/${workOrder.id}`}
                  >
                    {workOrder.name}
                  </Link>
                </span>
              </TableCell>
              <TableCell>
                <WorkTasks tasks={workOrder?.tasks} width="10rem" />
              </TableCell>
              <TableCell>{workOrder.workTemplate?.name || '-'}</TableCell>
              <TableCell>
                <WorkOrderStatusPill
                  className="table"
                  statusId={workOrder.statusId}
                />
              </TableCell>
              <TableCell>
                <WorkUsers
                  users={rollUpTaskUsers(workOrder.tasks as any)}
                  userGroups={workOrder.tasks.reduce(
                    (acc: WorkOrderUserGroup[], task) => [
                      ...acc,
                      ...task.userGroups,
                    ],
                    [],
                  )}
                />
              </TableCell>
              <TableCell>{setDisplayDate(workOrder.dueDate)}</TableCell>
              <TableCell>{setDisplayDate(workOrder.completionDate)}</TableCell>
              <TableCell>{setDisplayDate(workOrder.appliedDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={RESULTS_PER_PAGE_OPTIONS}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onPageChange={page => setCurrentPage(page)}
              onRowsPerPageChange={event => {
                setRowsPerPage(+event.target.value);
                setCurrentPage(1);
              }}
            />
          </TableRow>
        </TableFooter>
      </ListTable>
    </>
  );
};

export default AssetWorkOrdersTable;
